/**
 * useEmailTemplatesState - Custom hook for Email Templates (Welcome Email) module state management
 * Extracted from Admin.jsx to reduce state complexity
 * 
 * No changes to behavior, API endpoints, or request payloads.
 */
import { useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../../utils/apiUrl';

const defaultWelcomeEmailSettings = {
  enabled: true,
  subject: "Welcome to the Golf Nuts Clubhouse 🏌️",
  fromName: "Golf Nuts",
  headerImage: "",
  headerLinkUrl: "https://golfnutsofficial.com",
  bodyRichText: "",
  footerText: "© 2024 Golf Nuts. All rights reserved.",
  socialLinks: [
    { label: "Instagram", url: "https://instagram.com/golfnutsofficial", icon: "instagram", enabled: true },
    { label: "YouTube", url: "https://youtube.com/@golfnutsofficial", icon: "youtube", enabled: true },
    { label: "X (Twitter)", url: "https://x.com/golfnutsofficial", icon: "x", enabled: true },
    { label: "Spotify", url: "https://open.spotify.com/show/golfnuts", icon: "spotify", enabled: true },
    { label: "Apple Podcasts", url: "https://podcasts.apple.com/us/podcast/golf-nuts", icon: "apple", enabled: true }
  ],
  previewTestEmailRecipient: ""
};

export const useEmailTemplatesState = ({ setError, setSuccess }) => {
  // Welcome Email state
  const [welcomeEmailSettings, setWelcomeEmailSettings] = useState(null);
  const [loadingEmailSettings, setLoadingEmailSettings] = useState(false);
  const [emailSettingsHasChanges, setEmailSettingsHasChanges] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testEmailRecipient, setTestEmailRecipient] = useState('');
  const [emailPreviewHtml, setEmailPreviewHtml] = useState('');

  // Fetch welcome email settings
  const fetchWelcomeEmailSettings = useCallback(async () => {
    setLoadingEmailSettings(true);
    try {
      const response = await axios.get(`${API_URL}/api/cms/email-templates/welcome-email`);
      setWelcomeEmailSettings(response.data);
      setEmailSettingsHasChanges(false);
    } catch (err) {
      console.error('Error fetching welcome email settings:', err);
      // Set defaults if not found
      setWelcomeEmailSettings({ ...defaultWelcomeEmailSettings });
    } finally {
      setLoadingEmailSettings(false);
    }
  }, []);

  // Handle field changes
  const handleWelcomeEmailFieldChange = useCallback((field, value) => {
    setWelcomeEmailSettings(prev => prev ? ({
      ...prev,
      [field]: value
    }) : null);
    setEmailSettingsHasChanges(true);
  }, []);

  // Handle social link changes
  const handleWelcomeEmailSocialLinkChange = useCallback((index, field, value) => {
    setWelcomeEmailSettings(prev => {
      if (!prev) return prev;
      const newLinks = [...prev.socialLinks];
      newLinks[index] = { ...newLinks[index], [field]: value };
      return { ...prev, socialLinks: newLinks };
    });
    setEmailSettingsHasChanges(true);
  }, []);

  // Add new social link
  const handleAddWelcomeEmailSocialLink = useCallback(() => {
    setWelcomeEmailSettings(prev => prev ? ({
      ...prev,
      socialLinks: [
        ...prev.socialLinks,
        { _key: `sl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, label: "", url: "", icon: "instagram", enabled: true }
      ]
    }) : null);
    setEmailSettingsHasChanges(true);
  }, []);

  // Remove social link
  const handleRemoveWelcomeEmailSocialLink = useCallback((index) => {
    setWelcomeEmailSettings(prev => prev ? ({
      ...prev,
      socialLinks: prev.socialLinks.filter((_, i) => i !== index)
    }) : null);
    setEmailSettingsHasChanges(true);
  }, []);

  // Save settings
  const handleSaveWelcomeEmailSettings = useCallback(async () => {
    try {
      await axios.put(`${API_URL}/api/cms/email-templates/welcome-email`, welcomeEmailSettings);
      setEmailSettingsHasChanges(false);
      setSuccess('Welcome email settings saved successfully!');
    } catch (err) {
      console.error('Error saving welcome email settings:', err);
      setError('Failed to save welcome email settings: ' + (err.response?.data?.detail || err.message));
    }
  }, [welcomeEmailSettings, setError, setSuccess]);

  // Send test email
  const handleSendTestEmail = useCallback(async () => {
    if (!testEmailRecipient || !testEmailRecipient.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    
    setSendingTestEmail(true);
    try {
      // Save settings first
      await axios.put(`${API_URL}/api/cms/email-templates/welcome-email`, welcomeEmailSettings);
      
      // Send test email
      const response = await axios.post(`${API_URL}/api/cms/email-templates/welcome-email/send-test`, {
        recipientEmail: testEmailRecipient
      });
      setSuccess(response.data.message || 'Test email sent successfully!');
    } catch (err) {
      console.error('Error sending test email:', err);
      setError('Failed to send test email: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSendingTestEmail(false);
    }
  }, [testEmailRecipient, welcomeEmailSettings, setError, setSuccess]);

  // Fetch email preview
  const fetchEmailPreview = useCallback(async () => {
    try {
      // Save settings first to get accurate preview
      await axios.put(`${API_URL}/api/cms/email-templates/welcome-email`, welcomeEmailSettings);
      const response = await axios.get(`${API_URL}/api/cms/email-templates/welcome-email/preview`);
      setEmailPreviewHtml(response.data.html);
    } catch (err) {
      console.error('Error fetching email preview:', err);
    }
  }, [welcomeEmailSettings]);

  return {
    // State
    welcomeEmailSettings,
    loadingEmailSettings,
    emailSettingsHasChanges,
    sendingTestEmail,
    testEmailRecipient,
    emailPreviewHtml,

    // Setters
    setWelcomeEmailSettings,
    setEmailSettingsHasChanges,
    setTestEmailRecipient,
    setEmailPreviewHtml,

    // Actions
    fetchWelcomeEmailSettings,
    handleWelcomeEmailFieldChange,
    handleWelcomeEmailSocialLinkChange,
    handleAddWelcomeEmailSocialLink,
    handleRemoveWelcomeEmailSocialLink,
    handleSaveWelcomeEmailSettings,
    handleSendTestEmail,
    fetchEmailPreview,
  };
};

export default useEmailTemplatesState;
